import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { applyFieldFilter } from '../../../../shared/utils.js/fieldValidation';
import i18n from '../../../../i18n/i18n';
import { getEngineerDetails, updateEngineer } from '../../../../redux/Slices/EngineersSlice';

const EditEngineer = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isRTL = i18n.language === 'ar';
  const [form, setForm] = useState({
    name_ar: '',
    name_en: '',
    tax_number: '',
    address: '',
    phone: '',
    email: '',
    is_active: true,
  });

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        const payload = await dispatch(getEngineerDetails(id)).unwrap();
        const details = payload?.data || payload;
        setForm({
          name_ar: details?.name_ar || '',
          name_en: details?.name_en || '',
          tax_number: details?.tax_number || '',
          address: details?.address || '',
          phone: details?.phone || '',
          email: details?.email || '',
          is_active: Boolean(details?.is_active),
        });
      } catch (error) {
        toast.error(error || t('engineers.edit.messages.fetchError'), { rtl: isRTL });
        navigate('/engineers');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [dispatch, id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
    setForm((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await dispatch(updateEngineer({ id, engineerData: form })).unwrap();
      toast.success(t('engineers.edit.messages.updateSuccess'), { rtl: isRTL });
      navigate('/engineers');
    } catch (error) {
      toast.error(error || t('engineers.edit.messages.updateError'), { rtl: isRTL });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ ...cardStyle, textAlign: isRTL ? 'right' : 'left' }}>{t('engineers.view.loading')}</div>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...cardStyle, textAlign: isRTL ? 'right' : 'left' }}>
        <h2 style={{ marginBottom: 20, fontSize: '20px' }}>{t('engineers.edit.title')}</h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={gridStyle}>
            {isRTL ? (
              <>
                <Input label={t('engineers.edit.nameAr')} name="name_ar" value={form.name_ar} onChange={handleChange} required isRTL={true} />
                <Input label={t('engineers.edit.nameEn')} name="name_en" value={form.name_en} onChange={handleChange} required isRTL={false} />
              </>
            ) : (
              <>
                <Input label={t('engineers.edit.nameEn')} name="name_en" value={form.name_en} onChange={handleChange} required isRTL={false} />
                <Input label={t('engineers.edit.nameAr')} name="name_ar" value={form.name_ar} onChange={handleChange} required isRTL={true} />
              </>
            )}
          </div>
          <div style={gridStyle}>
            <Input label={t('engineers.edit.taxNumber')} name="tax_number" value={form.tax_number} onChange={handleChange} isRTL={isRTL} />
            <Input label={t('engineers.edit.phone')} name="phone" value={form.phone} onChange={handleChange} isRTL={isRTL} />
          </div>
          <Input label={t('engineers.edit.email')} name="email" value={form.email} onChange={handleChange} isRTL={false} />
          <Input label={t('engineers.edit.address')} name="address" value={form.address} onChange={handleChange} isRTL={isRTL} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, direction: isRTL ? 'rtl' : 'ltr' }}>
            <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} style={{ margin: isRTL ? '0 0 0 8px' : '0 8px 0 0' }} />
            {t('engineers.edit.active')}
          </label>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <button type="submit" style={submitButtonStyle} disabled={isSubmitting}>
              {isSubmitting ? t('engineers.edit.saving') : t('engineers.edit.save')}
            </button>
            <button type="button" style={secondaryButtonStyle} onClick={() => navigate('/engineers')}>
              {t('engineers.edit.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Input = ({ label, isRTL = true, ...rest }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 14 }}>{label}</span>
    <input
      {...rest}
      style={{
        padding: 12,
        borderRadius: 8,
        border: '1px solid #333',
        backgroundColor: '#1a1f2e',
        color: 'white',
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
      }}
    />
  </label>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 700,
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const submitButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

export default EditEngineer;

